/**
 * Inventaire des ouvrages à métrer — relevé du CCTP, croisé à la DPGF.
 *
 * Le métreur lance l'inventaire : la flotte lit le CCTP de l'instantané (source
 * PRIMAIRE), en dresse les ouvrages à métrer, chacun ancré à son article, et les
 * croise à la DPGF (validation seule). Ils s'affichent en direct (SSE), groupés.
 * Le métreur VALIDE ou REJETTE chacun — ou tout d'un coup ; ses verdicts se
 * gardent (base). L'inventaire dressé est mis en cache : rouvrir l'onglet le
 * montre aussitôt, sans le recalculer.
 *
 * On dit QUOI métrer, jamais COMBIEN : aucune quantité ici.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { Avis } from "../../composants/avis";

type Statut = "propose" | "valide" | "rejete";

interface Citation {
  lot: string; lot_titre?: string | null; article: string;
  page?: number | null; extrait: string;
}
interface Dpgf {
  designation?: string | null; unite?: string | null;
  reference?: string | null; score?: number | null;
}
interface Ouvrage {
  cle: string; libelle: string; unite: string; groupe: string;
  confiance: "haute" | "moyenne" | "faible";
  citations: Citation[]; dpgf: Dpgf | null; croise_dpgf: boolean;
  statut: Statut;
}

type Filtre = "tous" | "propose" | "valide" | "rejete" | "dpgf";

const FILTRES: { clef: Filtre; libelle: string }[] = [
  { clef: "tous", libelle: "Tous" },
  { clef: "propose", libelle: "En attente" },
  { clef: "valide", libelle: "Validés" },
  { clef: "rejete", libelle: "Rejetés" },
  { clef: "dpgf", libelle: "Croisés DPGF" },
];

export function Inventaire({ affaireId, onEchanger }: {
  affaireId: string; onEchanger?: (amorce: string) => void;
}) {
  const [ouvrages, poserOuvrages] = useState<Map<string, Ouvrage>>(new Map());
  const [lance, poserLance] = useState<boolean | null>(null); // null = pas encore su
  const [enMarche, poserEnMarche] = useState(false);
  const [prep, poserPrep] = useState("");
  const [progres, poserProgres] = useState<{ faits: number; total: number }>(
    { faits: 0, total: 0 });
  const [bilan, poserBilan] = useState<{ total: number; croises_dpgf: number } | null>(null);
  const [erreur, poserErreur] = useState("");
  const [filtre, poserFiltre] = useState<Filtre>("tous");
  const source = useRef<EventSource | null>(null);

  useEffect(() => () => source.current?.close(), []);

  // Au montage : l'inventaire déjà en cache (ou rien).
  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const r = await api.lire<{ donnees: Ouvrage[] | null }>(
          `/api/v1/affaires/${affaireId}/inventaire`);
        if (!vivant) return;
        if (r.donnees) {
          poserOuvrages(new Map(r.donnees.map((o) => [o.cle, o])));
          poserLance(true);
        } else {
          poserLance(false);
        }
      } catch {
        if (vivant) poserLance(false);
      }
    })();
    return () => { vivant = false; };
  }, [affaireId]);

  function lancer() {
    source.current?.close();
    poserOuvrages(new Map());
    poserBilan(null);
    poserErreur("");
    poserProgres({ faits: 0, total: 0 });
    poserPrep("Lecture du CCTP de l'instantané…");
    poserEnMarche(true);
    poserLance(true);
    const flux = new EventSource(
      `/api/v1/affaires/${affaireId}/inventaire/flux`);
    source.current = flux;
    flux.onmessage = (msg) => {
      const ev = JSON.parse(msg.data);
      if (ev.genre === "preparation") {
        poserPrep("Lecture du CCTP de l'instantané…");
      } else if (ev.genre === "cadre") {
        poserPrep(`Inventaire de ${ev.lots} lot(s), ${ev.articles} articles…`);
        poserProgres({ faits: 0, total: ev.paquets || 0 });
      } else if (ev.genre === "ouvrage") {
        const o = ev.ouvrage as Ouvrage;
        poserOuvrages((m) => new Map(m).set(o.cle, o));
      } else if (ev.genre === "progres") {
        poserProgres({ faits: ev.faits || 0, total: ev.total || 0 });
      } else if (ev.genre === "bilan") {
        poserBilan({ total: ev.total || 0, croises_dpgf: ev.croises_dpgf || 0 });
      } else if (ev.genre === "echec") {
        poserErreur(ev.motif || "L'inventaire a échoué.");
      }
    };
    flux.addEventListener("fin", () => {
      flux.close(); source.current = null;
      poserEnMarche(false); poserPrep("");
    });
    flux.addEventListener("echec", (msg) => {
      flux.close(); source.current = null;
      poserEnMarche(false); poserPrep("");
      try { poserErreur(JSON.parse((msg as MessageEvent).data).motif); }
      catch { poserErreur("L'inventaire a échoué."); }
    });
    flux.onerror = () => {
      // La fermeture normale (event: fin / echec) a déjà mis `source.current` à
      // null ; un onerror qui pointe encore CE flux est une vraie interruption.
      if (source.current === flux) {
        flux.close(); source.current = null; poserEnMarche(false); poserPrep("");
        poserErreur("Le flux d'inventaire s'est interrompu — relancez.");
      }
    };
  }

  async function verdict(cle: string, vise: Statut, libelle: string) {
    const courant = ouvrages.get(cle)?.statut ?? "propose";
    // Recliquer le même verdict = revenir à « en attente ».
    const statut: Statut = courant === vise ? "propose" : vise;
    poserOuvrages((m) => {
      const o = m.get(cle); if (!o) return m;
      return new Map(m).set(cle, { ...o, statut });
    });
    try {
      await api.remplacer(`/api/v1/affaires/${affaireId}/inventaire/verdict`,
        { cle, statut, apercu: libelle });
    } catch (exc) {
      poserOuvrages((m) => {
        const o = m.get(cle); if (!o) return m;
        return new Map(m).set(cle, { ...o, statut: courant });
      });
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "Le verdict n'a pas pu être enregistré.");
    }
  }

  async function verdictEnLot(vise: Statut, cibles: Ouvrage[]) {
    if (!cibles.length) return;
    const cles = cibles.map((o) => o.cle);
    const apercus = Object.fromEntries(cibles.map((o) => [o.cle, o.libelle]));
    poserOuvrages((m) => {
      const n = new Map(m);
      for (const o of cibles) n.set(o.cle, { ...o, statut: vise });
      return n;
    });
    try {
      await api.ecrire(`/api/v1/affaires/${affaireId}/inventaire/verdicts`,
        { statut: vise, cles, apercus });
    } catch (exc) {
      poserErreur(exc instanceof ErreurApi ? exc.message
        : "Les verdicts n'ont pas pu être enregistrés.");
    }
  }

  const tous = useMemo(() => [...ouvrages.values()], [ouvrages]);
  const compte = useMemo(() => ({
    total: tous.length,
    valide: tous.filter((o) => o.statut === "valide").length,
    rejete: tous.filter((o) => o.statut === "rejete").length,
    propose: tous.filter((o) => o.statut === "propose").length,
    dpgf: tous.filter((o) => o.croise_dpgf).length,
  }), [tous]);

  const visibles = useMemo(() => tous.filter((o) =>
    filtre === "tous" ? true
      : filtre === "dpgf" ? o.croise_dpgf
        : o.statut === filtre), [tous, filtre]);

  // Groupés, l'ordre des groupes suivant leur première apparition.
  const groupes = useMemo(() => {
    const m = new Map<string, Ouvrage[]>();
    for (const o of visibles) {
      const g = o.groupe || "Divers";
      const arr = m.get(g);
      if (arr) arr.push(o);
      else m.set(g, [o]);
    }
    return [...m.entries()];
  }, [visibles]);

  function echanger(o: Ouvrage) {
    const cit = o.citations[0];
    const ancre = cit ? `lot ${cit.lot}, article ${cit.article}` : "le CCTP";
    const amorce =
      `À propos de l'ouvrage à métrer « ${o.libelle} » (${o.unite}, groupe `
      + `${o.groupe}), relevé du CCTP (${ancre})`
      + (o.croise_dpgf ? " et croisé en DPGF" : " — non trouvé en DPGF")
      + ". Peux-tu confirmer qu'il est bien à métrer, préciser son mode de "
      + "métré (règle de déduction des vides, unité) et sa localisation ? ";
    onEchanger?.(amorce);
  }

  return (
    <div className="pile">
      <div className="carte">
        <div className="carte__corps pile pile--serree">
          <div className="rangee rangee--espacee">
            <div className="pile pile--serree">
              <h3 style={{ margin: 0 }}>Inventaire des ouvrages à métrer</h3>
              <span className="discret">
                Relevé du CCTP (source primaire), croisé à la DPGF. Le catalogue
                du dossier sert de référence.
              </span>
            </div>
            <button type="button"
                    className={`bouton ${enMarche ? "bouton--discret" : "bouton--primaire"}`}
                    disabled={enMarche}
                    onClick={lancer}>
              {enMarche ? "Inventaire en cours…"
                : lance ? "Relancer l'inventaire" : "Lancer l'inventaire"}
            </button>
          </div>

          {prep && <Avis ton="info" permanent>{prep}</Avis>}
          {erreur && <Avis ton="erreur" permanent>{erreur}</Avis>}
          {enMarche && progres.total > 0 && (
            <div className="inv-progres" aria-label="Progression">
              <div className="inv-progres__piste">
                <div className="inv-progres__barre"
                     style={{ width: `${Math.round(100 * progres.faits / progres.total)}%` }} />
              </div>
              <span className="discret">{progres.faits} / {progres.total} paquets</span>
            </div>
          )}

          {(lance || tous.length > 0) && (
            <div className="rangee rangee--espacee" style={{ flexWrap: "wrap", gap: "var(--mo-space-2)" }}>
              <div className="inv-filtres" role="group" aria-label="Filtres">
                {FILTRES.map((f) => (
                  <button key={f.clef} type="button"
                          className={`inv-chip${filtre === f.clef ? " inv-chip--actif" : ""}`}
                          aria-pressed={filtre === f.clef}
                          onClick={() => poserFiltre(f.clef)}>
                    {f.libelle}
                    <span className="inv-chip__n">
                      {f.clef === "tous" ? compte.total
                        : f.clef === "dpgf" ? compte.dpgf
                          : compte[f.clef]}
                    </span>
                  </button>
                ))}
              </div>
              {visibles.length > 0 && (
                <div className="rangee" style={{ gap: "var(--mo-space-2)" }}>
                  <button type="button" className="bouton bouton--discret"
                          onClick={() => verdictEnLot("valide", visibles)}>
                    ✓ Valider {filtre === "tous" ? "tout" : "la sélection"}
                  </button>
                  <button type="button" className="bouton bouton--discret"
                          onClick={() => verdictEnLot("rejete", visibles)}>
                    ✗ Rejeter {filtre === "tous" ? "tout" : "la sélection"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {lance === false && !enMarche && tous.length === 0 && (
        <div className="carte"><div className="carte__corps">
          <Avis ton="info" permanent>
            Aucun inventaire n'a encore été dressé pour cet instantané. Cliquez
            « Lancer l'inventaire » — la flotte lit le CCTP et en relève les
            ouvrages à métrer, croisés à la DPGF.
          </Avis>
        </div></div>
      )}

      {groupes.map(([groupe, items]) => (
        <div className="carte" key={groupe}>
          <div className="carte__corps pile pile--serree">
            <h4 className="inv-groupe">{groupe} <span className="discret">({items.length})</span></h4>
            <div className="defilant">
              <table className="tableau inv-table">
                <thead>
                  <tr>
                    <th>Ouvrage</th><th>Unité</th><th>Source CCTP</th>
                    <th>DPGF</th><th>Confiance</th><th>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((o) => (
                    <tr key={o.cle}
                        className={o.statut === "rejete" ? "inv-ligne--rejete"
                          : o.statut === "valide" ? "inv-ligne--valide" : ""}>
                      <td>
                        <span className="inv-libelle">{o.libelle}</span>
                        {onEchanger && (
                          <button type="button" className="inv-lien"
                                  onClick={() => echanger(o)}
                                  title="Échanger avec les agents">échanger</button>
                        )}
                      </td>
                      <td className="mono">{o.unite || "—"}</td>
                      <td>
                        {o.citations.map((c, i) => (
                          <span key={i} className="inv-ancre"
                                title={c.extrait}>
                            Lot {c.lot} · art {c.article}
                            {c.page != null ? ` · p.${c.page}` : ""}
                          </span>
                        ))}
                      </td>
                      <td>
                        {o.croise_dpgf ? (
                          <span className="inv-badge inv-badge--dpgf"
                                title={o.dpgf?.reference || ""}>
                            ✓ {o.dpgf?.designation || "DPGF"}
                          </span>
                        ) : (
                          <span className="inv-badge inv-badge--muet">hors DPGF</span>
                        )}
                      </td>
                      <td>
                        <span className={`inv-conf inv-conf--${o.confiance}`}>
                          {o.confiance}
                        </span>
                      </td>
                      <td>
                        <div className="inv-verdict">
                          <button type="button"
                                  className={`inv-vbtn inv-vbtn--ok${o.statut === "valide" ? " inv-vbtn--actif" : ""}`}
                                  aria-pressed={o.statut === "valide"}
                                  onClick={() => verdict(o.cle, "valide", o.libelle)}
                                  title="Valider">✓</button>
                          <button type="button"
                                  className={`inv-vbtn inv-vbtn--ko${o.statut === "rejete" ? " inv-vbtn--actif" : ""}`}
                                  aria-pressed={o.statut === "rejete"}
                                  onClick={() => verdict(o.cle, "rejete", o.libelle)}
                                  title="Rejeter">✗</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ))}

      {bilan && !enMarche && (
        <div className="carte"><div className="carte__corps">
          <span className="discret">
            {bilan.total} ouvrage(s) inventorié(s) — {bilan.croises_dpgf} croisé(s)
            en DPGF · {compte.valide} validé(s), {compte.rejete} rejeté(s),
            {" "}{compte.propose} en attente.
          </span>
        </div></div>
      )}
    </div>
  );
}
